class WaveText {
    constructor(element, options = {}) {
        this.element = element;
        this.options = Object.assign({
            amplitude: -15,
            waveLength: 300,
            offset: 0,
            animate: false,
            showPath: false,
            slideDirection: 'right', // 'left' or 'right'  
            slideDuration: 1200,
            slideEasing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            autoInit: true,
            trigger: 'scroll', // 'scroll', 'manual', or 'delay'
            delay: 0,
            threshold: 0.3
        }, options);

        this.isInitialized = false;
        this.isAnimating = false;
        this.animationFrame = null;
        this.slideAnimationFrame = null;
        this.observer = null;
        this.currentOffset = 0;

        if (this.options.autoInit) {
            this.init();
        }
    }

    init() {
        if (this.isInitialized) return;

        this.prepareSpans();
        this.calculateStartOffset();
        this.applyWave();
        
        // Add visible class now that text is positioned off-screen
        this.element.parentElement.classList.add('visible');
        
        this.setupTrigger();
        this.isInitialized = true;
    }

    calculateStartOffset() {
        const containerWidth = this.element.parentElement.offsetWidth;
        let textWidth = 0;
        
        this.spans.forEach(span => {
            textWidth += span.offsetWidth;
        });
        
        // Calculate center offset (where text should end up)
        this.centerOffset = (containerWidth - textWidth) / 2;
        
        // Start offset should position text just outside the container edge
        // Only need enough offset to hide the text, not way off-screen
        if (this.options.slideDirection === 'right') {
            this.currentOffset = containerWidth / 2 + textWidth / 2 + 50;
        } else {
            this.currentOffset = -(containerWidth / 2 + textWidth / 2 + 50);
        }
    }

    prepareSpans() {
        this.text = this.element.innerText;
        this.element.innerHTML = '';
        
        for (let i = 0; i < this.text.length; i++) {
            const span = document.createElement('span');
            span.innerText = this.text[i] === " " ? "\u00A0" : this.text[i];
            span.style.display = 'inline-block';
            this.element.appendChild(span);
        }

        this.spans = this.element.querySelectorAll('span');

        const existingPath = this.element.querySelector('.wave-path');
        if (existingPath) {
            this.element.removeChild(existingPath);
        }
    }

    setupTrigger() {
        switch (this.options.trigger) {
            case 'scroll':
                this.setupScrollTrigger();
                break;
            case 'delay':
                setTimeout(() => this.start(), this.options.delay);
                break;
            case 'manual':
                break;
        }
    }

    setupScrollTrigger() {
        if (!window.IntersectionObserver) {
            this.start();
            return;
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.isAnimating) {
                    this.start();
                }
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: '0px'
        });

        this.observer.observe(this.element);
    }

    start() {
        if (this.isAnimating) return;
        this.isAnimating = true;

        // Recalculate in case window was resized
        this.calculateStartOffset();
        
        const startOffset = this.currentOffset;
        const endOffset = 0; // Always end at 0 for symmetric wave
        const startTime = performance.now();

        const slideAnimation = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / this.options.slideDuration, 1);
            
            // Easing function - starts fast, slows down dramatically
            const easeProgress = this.easeOutQuart(progress);
            
            this.currentOffset = startOffset + (endOffset - startOffset) * easeProgress;
            this.applyWave();

            if (progress < 1) {
                this.slideAnimationFrame = requestAnimationFrame(slideAnimation);
            } else {
                if (this.options.animate) {
                    this.animate();
                }
            }
        };

        this.slideAnimationFrame = requestAnimationFrame(slideAnimation);
    }

    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    }

    easeOutQuart(t) {
        return 1 - (--t) * t * t * t;
    }

    applyWave(time = 0) {
        const amplitude = this.options.amplitude;
        const waveLength = this.options.waveLength;
        const offset = this.currentOffset || this.options.offset;
        const frequency = (2 * Math.PI) / waveLength;

        let totalWidth = 0;
        this.spans.forEach(span => {
            totalWidth += span.offsetWidth;
        });

        const phase = Math.PI / 2 - frequency * (totalWidth / 2) + time;

        let currentX = 0;

        this.drawWavePath(amplitude, frequency, phase, totalWidth);

        this.spans.forEach((span, index) => {
            const originalX = currentX + span.offsetWidth / 2;
            const shiftedX = originalX + offset;
            const y = amplitude * Math.sin(frequency * shiftedX + phase);
            const angle = Math.atan(amplitude * frequency * Math.cos(frequency * shiftedX + phase));
            const degrees = angle * (180 / Math.PI);

            span.style.transform = `translateX(${offset}px) translateY(${y}px) rotate(${degrees}deg)`;
            currentX += span.offsetWidth;
        });
    }

    drawWavePath(amplitude, frequency, phase, totalWidth) {
        const existingPath = this.element.querySelector('.wave-path');
        if (existingPath) {
            this.element.removeChild(existingPath);
        }

        if (this.options.showPath) {
            const svgns = "http://www.w3.org/2000/svg";
            const svg = document.createElementNS(svgns, "svg");
            svg.setAttribute("class", "wave-path");
            svg.setAttribute("width", "100%");
            svg.setAttribute("height", "100%");
            svg.style.position = "absolute";
            svg.style.top = "0";
            svg.style.left = "0";

            const path = document.createElementNS(svgns, "path");

            const containerWidth = this.element.offsetWidth;
            const containerHeight = this.element.offsetHeight;
            const centerY = containerHeight / 2;

            let pathData = `M 0,${centerY}`;

            for (let x = 0; x <= containerWidth; x += 5) {
                const xPos = x - containerWidth/2 + (this.currentOffset || this.options.offset);
                const y = centerY + amplitude * Math.sin(frequency * xPos + phase);
                pathData += ` L ${x},${y}`;
            }

            path.setAttribute("d", pathData);
            path.setAttribute("stroke", "currentColor");
            path.setAttribute("stroke-width", "1");
            path.setAttribute("fill", "none");
            path.setAttribute("opacity", "0.3");

            svg.appendChild(path);
            this.element.appendChild(svg);
        }
    }

    animate() {
        this.stop();

        let time = 0;

        const animateWave = () => {
            time += 0.03;
            this.applyWave(time);
            this.animationFrame = requestAnimationFrame(animateWave);
        };

        this.animationFrame = requestAnimationFrame(animateWave);
    }

    stop() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        if (this.slideAnimationFrame) {
            cancelAnimationFrame(this.slideAnimationFrame);
            this.slideAnimationFrame = null;
        }
    }

    updateOptions(newOptions) {
        this.options = Object.assign(this.options, newOptions);
        this.currentOffset = this.options.offset;
        this.applyWave();

        if (this.options.animate) {
            this.animate();
        } else {
            this.stop();
        }
    }

    destroy() {
        this.stop();
        if (this.observer) {
            this.observer.disconnect();
        }
        this.element.innerHTML = this.text;
        this.isInitialized = false;
        this.isAnimating = false;
    }
}


/*
// Auto-initialize elements with data-wavetext attribute. Not needed here.
document.addEventListener('DOMContentLoaded', () => {
    const elements = document.querySelectorAll('[data-wavetext]');
    elements.forEach(element => {
        const options = {};
        const dataset = element.dataset;

        if (dataset.amplitude) options.amplitude = parseFloat(dataset.amplitude);
        if (dataset.wavelength) options.waveLength = parseFloat(dataset.wavelength);
        if (dataset.offset) options.offset = parseFloat(dataset.offset);
        if (dataset.animate) options.animate = dataset.animate === 'true';
        if (dataset.slideDirection) options.slideDirection = dataset.slideDirection;
        if (dataset.slideDuration) options.slideDuration = parseInt(dataset.slideDuration);
        if (dataset.trigger) options.trigger = dataset.trigger;
        if (dataset.delay) options.delay = parseInt(dataset.delay);
        if (dataset.threshold) options.threshold = parseFloat(dataset.threshold);

        new WaveText(element, options);
    });
});
*/

